import _ from 'lodash';
import * as actionTypes from 'constants/action-types';
import { MockServiceTemplateActionTypes } from 'store/mockServiceTemplates/mockServiceTemplatesActionTypes';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';
import { Filter } from '../mockServices/mockServiceTypes';

export interface MockServiceTemplatesState {
  mockServiceTemplates: MockServiceTemplate[];
  lastUpdate?: number;
  isLoading: boolean;
  isSaving: boolean;
  mockTemplateTableMeta: {
    servicesInProgress: number[];
  };

  /**
   * Id of the expanded template
   */
  expandedTemplateId: number | null;

  /**
   * Active page
   */
  activePage: number;

  /** Page filter */
  filter: Filter;
}

const DEFAULT_STATE: MockServiceTemplatesState = {
  mockServiceTemplates: [],
  isLoading: true,
  isSaving: false,
  lastUpdate: 0,
  mockTemplateTableMeta: {
    servicesInProgress: [],
  },
  expandedTemplateId: null,
  activePage: 1,
  filter: {
    search: null,
    shipId: null,
    location: null,
    status: null,
  },
};

export const mockServiceTemplatesReducer = (
  state: MockServiceTemplatesState = DEFAULT_STATE,
  action: MockServiceTemplateActionTypes,
): MockServiceTemplatesState => {
  switch (action.type) {
    /**
     *  Fetch templates finished.
     */
    case actionTypes.MOCK_SERVICE_FETCH_TEMPLATES:
      const sortedMockServiceTemplatesList = _.orderBy(action.payload, 'id', 'desc');
      return {
        ...state,
        isLoading: false,
        mockServiceTemplates: sortedMockServiceTemplatesList,
        lastUpdate: Date.now(),
        activePage: 1,
      };

    /**
     * Create Service started.
     */
    case actionTypes.MOCK_SERVICE_TEMPLATES_SERVICE_CREATION_STARTED:
      const creationStartedGriddleMeta = { ...state.mockTemplateTableMeta };
      creationStartedGriddleMeta.servicesInProgress.push(action.payload);
      return {
        ...state,
        mockTemplateTableMeta: creationStartedGriddleMeta,
      };

    /**
     * Create Service finished.
     */
    case actionTypes.MOCK_SERVICE_TEMPLATES_SERVICE_CREATION_FINISHED:
      const creationFinishedGriddleMeta = { ...state.mockTemplateTableMeta };

      //Get rid of any object with id > 0, it was saved already
      _.remove(creationFinishedGriddleMeta.servicesInProgress, item => item === 0);
      _.remove(creationFinishedGriddleMeta.servicesInProgress, item => item === action.payload);
      return {
        ...state,
        mockTemplateTableMeta: creationFinishedGriddleMeta,
      };

    case actionTypes.MOCK_SERVICE_TEMPLATES_UPDATE_FILTER: {
      return { ...state, filter: action.payload };
    }

    /**
     * Fetch templates started.
     */
    case actionTypes.MOCK_SERVICE_TEMPLATES_SHOW_LOADING_INDICATOR:
      return {
        ...state,
        isLoading: true,
      };

    /**
     * Sets saving state.
     */
    case actionTypes.MOCK_TEMPLATES_SET_SAVING_STATE:
      return {
        ...state,
        isSaving: action.payload,
      };

    /**
     * Change active page.
     */
    case actionTypes.MOCK_SERVICE_TEMPLATES_CHANGE_ACTIVE_PAGE:
      return {
        ...state,
        activePage: action.payload,
      };

    case actionTypes.MOCK_SERVICE_TEMPLATES_COLLAPSE_ALL: {
      return { ...state, expandedTemplateId: null };
    }
    /**
     * Update virtual service template
     */
    case actionTypes.MOCK_SERVICE_TEMPLATES_UPDATE: {
      const mockServiceTemplates = [...state.mockServiceTemplates];

      // Find index of this template
      const mockServiceIndex: number = mockServiceTemplates.findIndex((template: MockServiceTemplate) => {
        return action.payload.id === template.id;
      });

      // Replace it with the one from payload
      mockServiceTemplates[mockServiceIndex] = action.payload;

      return {
        ...state,
        mockServiceTemplates: mockServiceTemplates,
      };
    }

    /**
     * Override virtual service template
     */
    case actionTypes.SWAP_MOCK_SERVICE_TEMPLATE_DRAFT: {
      const overrideTemplateState = [...state.mockServiceTemplates];

      // Find index of this template
      const mockServiceIndex: number = overrideTemplateState.findIndex((template: MockServiceTemplate) => {
        return action.payload.id === template.id;
      });

      // Replace it with the one from payload
      overrideTemplateState[mockServiceIndex] = action.payload.template;

      // If this item is expanded, change id of expanded item
      let expandedId = state.expandedTemplateId;
      if (state.expandedTemplateId === action.payload.id) {
        expandedId = action.payload.template.id;
      }

      return {
        ...state,
        mockServiceTemplates: overrideTemplateState,
        expandedTemplateId: expandedId,
      };
    }

    /**
     * Expand item in the table.
     */
    case actionTypes.MOCK_SERVICE_TEMPLATES_EXPAND_ITEM: {
      const expandItemState = _.cloneDeep(state);
      if (expandItemState.expandedTemplateId === null || expandItemState.expandedTemplateId !== action.payload) {
        expandItemState.expandedTemplateId = action.payload;
      } else {
        expandItemState.expandedTemplateId = null;
      }
      return expandItemState;
    }

    /**
     * Delete virtual service template
     */
    case actionTypes.MOCK_SERVICE_TEMPLATES_DELETE: {
      let mockServiceTemplates = [...state.mockServiceTemplates];
      mockServiceTemplates = mockServiceTemplates.filter(
        (template: MockServiceTemplate) => template.id !== action.payload,
      );
      return {
        ...state,
        mockServiceTemplates: mockServiceTemplates,
      };
    }

    /**
     * Create blank template
     */
    case actionTypes.MOCK_SERVICE_TEMPLATES_CREATE_BLANK:
      // Add it to the list only in case there is no one yet
      let mockServiceTemplates = [...state.mockServiceTemplates];
      mockServiceTemplates = [action.payload, ...state.mockServiceTemplates];

      return {
        ...state,
        mockServiceTemplates: mockServiceTemplates,
        activePage: 1, // Always go to first page when creating blank item
      };

    /**
     * Update id of existing template
     */
    case actionTypes.MOCK_SERVICE_TEMPLATES_UPDATE_BY_ID: {
      const mockServiceTemplates = [...state.mockServiceTemplates];

      // Find index of this template
      const mockServiceIndex: number = mockServiceTemplates.findIndex((template: MockServiceTemplate) => {
        return action.payload.oldTemplateId === template.id;
      });

      // Replace it with the one from payload
      mockServiceTemplates[mockServiceIndex] = action.payload.newTemplate;

      return {
        ...state,
        mockServiceTemplates: mockServiceTemplates,
      };
    }

    default:
      return state;
  }
};
